import * as React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";
import { useRef, useState, useEffect } from "react";

const coinFlip = keyframes`
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;
const ringOut = keyframes`
  0%   { transform: scale(0.8); opacity: 0.8; }
  100% { transform: scale(2.1); opacity: 0; }
`;
const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
  40%           { transform: translateY(-8px); opacity: 1; }
`;
const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.0); }
  50%      { box-shadow: 0 0 0 4px rgba(255,255,255,0.35); }
`;

// A click is ignored for at least this long, even when the handler finishes at once or
// does not return a promise, so a double click can never submit twice.
const MIN_LOCK_MS = 1200;

/**
 * Drop-in replacement for the Button that commits a payment.
 *  - the first click locks the button immediately (a ref, not state, so a fast double click
 *    cannot slip through before React re-renders)
 *  - while the handler runs it shows a flipping rupee coin and "Processing…"
 *  - `loading` lets a parent that already tracks the save state keep it locked too
 *
 * Props: all Button props, plus
 *   ButtonComponent  styled Button to render (default: MUI Button)
 *   loading          external busy flag
 *   loadingLabel     text while busy (default "Processing…")
 *   overlay          also cover the whole screen with a payment animation while busy; use it
 *                    on the button that actually saves the payment, not on one that only
 *                    opens a confirm dialog
 */
export default function PayButton({
  onClick,
  loading = false,
  loadingLabel = "Processing…",
  ButtonComponent = Button,
  overlay = false,
  overlayText = "Processing payment",
  children,
  sx,
  startIcon,
  ...rest
}) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const handleClick = async (event) => {
    if (lock.current || loading) {
      event?.preventDefault?.();
      return;
    }
    lock.current = true;
    setBusy(true);
    const started = Date.now();
    try {
      await onClick?.(event);
    } finally {
      const wait = Math.max(0, MIN_LOCK_MS - (Date.now() - started));
      setTimeout(() => {
        lock.current = false;
        if (mounted.current) setBusy(false);
      }, wait);
    }
  };

  const working = busy || loading;

  return (
    <>
    {overlay && (
      <Backdrop
        open={working}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1000,
          flexDirection: "column", gap: 3, color: "#fff",
          backgroundColor: "rgba(10, 25, 20, 0.78)", backdropFilter: "blur(3px)",
        }}
      >
        <Box sx={{ position: "relative", width: 120, height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: "absolute", inset: 0, borderRadius: "50%",
                border: "3px solid rgba(255, 215, 90, 0.85)",
                animation: `${ringOut} 2s ease-out ${i * 0.65}s infinite`,
              }}
            />
          ))}
          <Box
            sx={{
              width: 84, height: 84, borderRadius: "50%",
              background: "radial-gradient(circle at 30% 30%, #ffe27a, #e0a800)",
              boxShadow: "0 0 24px rgba(255, 200, 40, 0.65)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#7a5200", fontSize: 44, fontWeight: 800,
              animation: `${coinFlip} 1.2s linear infinite`,
            }}
          >
            ₹
          </Box>
        </Box>
        <Box sx={{ textAlign: "center" }} role="status" aria-live="polite">
          <Typography variant="h6" sx={{ fontWeight: 700, display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
            {overlayText}
            {[0, 1, 2].map((i) => (
              <Box key={i} component="span" sx={{ animation: `${bounce} 1.2s ease-in-out ${i * 0.15}s infinite`, display: "inline-block" }}>.</Box>
            ))}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
            Please don't close or refresh this page
          </Typography>
        </Box>
      </Backdrop>
    )}
    <ButtonComponent
      {...rest}
      onClick={handleClick}
      aria-busy={working}
      startIcon={working ? undefined : startIcon}
      sx={{
        ...(sx || {}),
        ...(working
          ? {
              pointerEvents: "none",
              cursor: "progress",
              animation: `${glow} 1.2s ease-in-out infinite`,
            }
          : {}),
      }}
    >
      {working ? (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <Box
            component="span"
            sx={{
              width: 20, height: 20, borderRadius: "50%",
              border: "2px solid currentColor",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 800, lineHeight: 1,
              animation: `${coinFlip} 0.9s linear infinite`,
            }}
          >
            ₹
          </Box>
          <span>{loadingLabel}</span>
        </Box>
      ) : (
        children
      )}
    </ButtonComponent>
    </>
  );
}
